import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateWorkspaceInput, MeResponse, UpdateMeInput } from '@wrapforce/shared';
import { ApiError } from '@wrapforce/shared/errors';
import { api } from '@/lib/api';

// Unlike the global query retry policy (one retry after 1s), a stranded user
// here sees a dead-end error screen, so network/5xx failures get patient
// exponential backoff. Auth/client errors (401, other 4xx) still fail fast.
function shouldRetryMe(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status < 500 && error.code !== 'INTERNAL') {
    return false;
  }
  return failureCount < 4;
}

function retryDelayMe(attempt: number): number {
  return Math.min(1_000 * 2 ** attempt, 30_000);
}

export function useMe(enabled = true) {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/api/v1/me'),
    staleTime: 60_000,
    enabled,
    retry: shouldRetryMe,
    retryDelay: retryDelayMe,
  });
}

export function useUpdateMe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMeInput) => api.patch('/api/v1/me', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkspaceInput) => api.post<{ workspaceId: string }>('/api/v1/workspaces', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'], refetchType: 'all' }),
  });
}
